<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { useClickOutside } from '../../composables/useClickOutside'

const open = ref(false)
const wrapperRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const activeIndex = ref(-1)

useClickOutside(wrapperRef, () => { open.value = false })

function getMenuItems(): HTMLElement[] {
  if (!menuRef.value) return []
  return Array.from(menuRef.value.querySelectorAll<HTMLElement>('[role="menuitem"]'))
}

function focusItem(index: number) {
  const items = getMenuItems()
  if (items.length === 0) return
  activeIndex.value = Math.max(0, Math.min(index, items.length - 1))
  items[activeIndex.value]?.focus()
}

async function toggle() {
  open.value = !open.value
  if (open.value) {
    activeIndex.value = -1
    await nextTick()
    focusItem(0)
  }
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle()
    }
    return
  }

  const items = getMenuItems()
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      focusItem(activeIndex.value + 1)
      break
    case 'ArrowUp':
      e.preventDefault()
      focusItem(activeIndex.value - 1)
      break
    case 'Home':
      e.preventDefault()
      focusItem(0)
      break
    case 'End':
      e.preventDefault()
      focusItem(items.length - 1)
      break
    case 'Escape':
      e.preventDefault()
      close()
      break
  }
}

function close() {
  open.value = false
  activeIndex.value = -1
}

defineExpose({ close })
</script>

<template>
  <div ref="wrapperRef" class="relative inline-block" @keydown="onKeydown">
    <div @click="toggle">
      <slot name="trigger" />
    </div>
    <Transition
      enter-active-class="transition-all duration-150"
      leave-active-class="transition-all duration-100"
      enter-from-class="opacity-0 scale-95"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="open"
        ref="menuRef"
        role="menu"
        class="absolute right-0 mt-2 w-48 glass rounded shadow-[0_12px_24px_rgba(42,52,57,0.06)] z-50 py-1"
      >
        <slot :close="close" />
      </div>
    </Transition>
  </div>
</template>
